/**
 * Where a prospect without a trial code goes to get one.
 *
 * This is the website's own booking modal target, the Google Appointment
 * Schedule behind #ppDemoFrame in privacypal-web/assets/v3.js. Using the same
 * schedule matters: the desktop app and the Max landing page both send people
 * here, and three surfaces pointing at three different places is how a
 * prospect ends up on a dead link after sales moves the calendar.
 *
 * VITE_DEMO_BOOKING_URL overrides it per build. Do not point the fallback at
 * privacypal.ai/contact: that page is mailto: links with no scheduler on it,
 * so a "book a call" button landing there asks the prospect to do the work.
 */
const FALLBACK_BOOKING_URL =
  'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0en3ttxqL4LChfrMDCbYeqxtKuKXOR4YRnGz0Sfg0RIwh7nO51zW3MqxBnQzAVcSkQoy3_6UJZ?gv=true';

/**
 * The booking link, with context on the query string.
 *
 * Be clear about what this does and does not achieve. A Google Appointment
 * Schedule does not surface arbitrary query params onto the booking record,
 * so these params are NOT how a sales-assisted trial gets attributed. They
 * are there for referrer-level reporting and for whatever reads them later.
 *
 * Partner credit is recorded at SIGNUP, where register calls attributeReferral
 * and the first write wins per company. If a prospect books a call instead of
 * signing up, the partner is credited only when that company later signs up
 * carrying the same ref. Anyone wiring up commission reporting should read
 * Company.referralCode, never this URL.
 */
export function trialCallUrl({
  source,
  referralCode,
  trialCode,
}: { source?: string; referralCode?: string; trialCode?: string } = {}): string {
  const base = import.meta.env.VITE_DEMO_BOOKING_URL || FALLBACK_BOOKING_URL;
  try {
    const url = new URL(base);
    url.searchParams.set('interest', 'max-trial');
    if (source) url.searchParams.set('src', source);
    if (referralCode) url.searchParams.set('ref', referralCode);
    if (trialCode) url.searchParams.set('trial', trialCode);
    return url.toString();
  } catch {
    // A misconfigured env value must not break the button.
    return FALLBACK_BOOKING_URL;
  }
}
